define('home/models/User',[
    'jquery',
    'underscore',

    'core/api',
    'core/strings',
    'core/models/User',

    'home/utils/backboneUtils',
    'home/utils/confirmModal',

    'home/templates/confirmBlacklistUser',

    'core/UniqueModel',
], function (
    $,
    _,

    api,
    strings,
    CoreUser,

    backboneUtils,
    confirmModal,

    confirmBlacklistUserTemplate,

    UniqueModel
) {
    'use strict';

    var parent = CoreUser;
    var parentProto = parent.prototype;

    var gettext = strings.get;

    var SUPPORTED_MODERATE_ENTITIES = {
        'forum': true,
        'channel': true,
    };

    var User = parent.extend({
        defaults: _.defaults({
            // Unlike numForumsFollowing, numFollowing, or numFollowers,
            // numChannelsFollowing is not returned from users/details,
            // but derived from users/listFollowingChannels endpoint
            // (hence why default is not declared on the BaseUser class)
            numChannelsFollowing: null,
        }, parentProto.defaults),

        idAttribute: 'username',

        initialize: function (attributes, options) {
            parentProto.initialize.call(this, attributes, options);

            var Session = require('home/models/Session');
            this.session = Session.get();

            this.listenTo(this, 'followed:user', this.handleFollowedUser);
            this.listenTo(this, 'unfollowed:user', this.handleUnfollowedUser);
            this.listenTo(this, 'followed:forum', this.handleFollowedForum);
            this.listenTo(this, 'unfollowed:forum', this.handleUnfollowedForum);
            this.listenTo(this, 'followed:channel', this.handleFollowedChannel);
            this.listenTo(this, 'unfollowed:channel', this.handleUnfollowedChannel);
        },

        /*
         * Returns true if it looks like this user has not been fully
         * filled with data yet. Use this when showing a full profile
         * for a user.
         * @returns {boolean}
         */
        shouldFetch: function () {
            return !this.get('name') ||
                !this.has('numFollowers') ||
                !this.has('numFollowing') ||
                !this.has('numForumsFollowing');
        },

        /*
         * Returns true if it looks like this user does not have the
         * basic data fetched yet. Will not check for special aggreate
         * values used in showing a user's profile. Use this when showing
         * a user card.
         * @returns {boolean}
         */
        shouldFetchBasics: function () {
            return !this.get('name');
        },

        ensureFetchedBasics: function () {
            if (this.shouldFetchBasics())
                this.fetch();

            return this.fetchPromise || $.Deferred().resolve().promise();
        },

        // TODO: override this.isSession--
        //      parentProto.isSession.call(this, this.session);
        isSessionUser: function () {
            return this.isSession(this.session);
        },

        // DisqusNetMod is a subset of Disqus Admins that can see
        // moderation tools within home
        isGlobalAdmin: function () {
            return this.session.user.get('isGlobalAdmin');
        },

        // Copies logic from disqus-web for friendly labels for user reputation
        // Only visible for DisqusNetMods
        getUserReputationLabel: function () {
            var rep = this.get('rep');

            if (rep < 0.25)
                return 'Low 1';
            else if (rep < 0.8)
                return 'Low 2';
            else if (rep < 2.9)
                return 'Average';

            return 'High';
        },

        prepareFetchOptions: function (options) {
            options = options ? _.clone(options) : {};
            options.data = _.extend({ attach: 'userFlaggedUser' }, options.data);
            return parentProto.prepareFetchOptions.call(this, options);
        },

        fetch: function (options) {
            options = this.prepareFetchOptions(options);
            return backboneUtils.memoizedFetch.call(this, options);
        },

        getNumTotalFollowing: function () {
            return this.get('numFollowing') +
                this.getNumCommunitiesFollowing();
        },

        getNumCommunitiesFollowing: function () {
            return this.get('numForumsFollowing') + this.get('numChannelsFollowing');
        },

        toggleFollowed: function () {
            return this.get('isFollowing') ?
                this.unfollow() :
                this.follow();
        },

        /*
         * Allows the session user to follow this user
         */
        follow: function () {
            this.session.user.trigger('followed:user', this);
            return parentProto.follow.call(this);
        },

        /*
         * Allows the session user to unfollow this user
         */
        unfollow: function () {
            this.session.user.trigger('unfollowed:user', this);
            return parentProto.unfollow.call(this);
        },

        /*
         * Performs the necessary api call to break the "other user
         * follows this user" relationship.
         * @param  {Model} user The user following this user
         * @returns {Promise}      The jQuery ajax request promise
         */
        removeFollower: function (user) {
            // Only works for the currently authenticated user.
            if (!this.isSessionUser())
                return;

            var numFollowers = this.get('numFollowers') - 1;
            this.set('numFollowers', Math.max(0, numFollowers));

            return api.call('users/removeFollower', {
                type: 'POST',
                data: {
                    follower: user.get('id'),
                },
            });
        },

        /**
         * Updates the necessary user data when this user has
         * followed another user
         */
        handleFollowedUser: function () {
            this.set({
                numFollowing: this.get('numFollowing') + 1,
            });
        },

        /**
         * Updates the necessary user data when this user has
         * unfollowed another user
         */
        handleUnfollowedUser: function () {
            this.set({
                numFollowing: Math.max(0, this.get('numFollowing') - 1),
            });
        },

        /**
         * Updates the necessary user data when this user has
         * followed a forum
         */
        handleFollowedForum: function () {
            this.set({
                numForumsFollowing: this.get('numForumsFollowing') + 1,
            });
        },

        /**
         * Updates the necessary user data when this user has
         * unfollowed a forum
         */
        handleUnfollowedForum: function () {
            this.set({
                numForumsFollowing: Math.max(0, this.get('numForumsFollowing') - 1),
            });
        },

        handleFollowedChannel: function () {
            this.set({
                numChannelsFollowing: this.get('numChannelsFollowing') + 1,
            });
        },

        handleUnfollowedChannel: function () {
            this.set({
                numChannelsFollowing: Math.max(0, this.get('numChannelsFollowing') - 1),
            });
        },

        /**
         * Add or remove the user as a moderator of a supported entity
         *
         * @param {Object} opts - Options supplied to moderating an entity
         * @param {Object} opts.entity - The model used to handle the toggle trigger
         * @param {string} opts.type - Type of entity
         * @param {string} opts.endpoint - Moderation endpoint for model
         * @param {string} opts.forumId - Id of the forum this model belongs to
         * @param {boolean} opts.shouldModerate - Id of the forum this model belongs to
         *
         * @returns {Promise} The promise object for the moderation API call
         */
        moderate: function (opts) {
            if (!SUPPORTED_MODERATE_ENTITIES[opts.type])
                return;

            var action = opts.shouldModerate ? 'addModerator' : 'removeModerator';
            var url = opts.endpoint + '/' + action;
            var data = { user: 'username:' + this.get('username') };
            data[opts.type] = opts.entity.id;

            return $.when(
                // Ensure user basics have been fetched before triggering the
                // add/remove event. When triggering remove this won't kick
                // off a fetch since by the time we call remove we have basic
                // user details. When triggering add, want to ensure we have
                // user's name and avatar fetched so handlers of the addModerator
                // event will get a ready-to-display user.
                this.ensureFetchedBasics(),

                // Don't need to wait for user fetch to return to call add/remove
                // as all that's needed is the username.
                api.call(url, { method: 'POST', data: data })
            ).done(_.bind(function () {
                // Add/remove channel's primary forum to/from the list of forums this user moderates
                var moderatedForums = this.get('moderatedForums') || [];
                if (opts.shouldModerate) {
                    moderatedForums.push(opts.forumId);
                } else {
                    moderatedForums = _.reject(moderatedForums, function (moderatedForumId) {
                        return moderatedForumId === opts.forumId;
                    });
                }

                this.set('moderatedForums', moderatedForums);

                // Let channel handle the addition/removal of a moderator
                opts.entity.trigger(action, this);
            }, this));
        },

        moderateForum: function (forum, shouldModerate) {
            return this.moderate({
                entity: forum,
                type: 'forum',
                endpoint: 'forums',
                forumId: forum.id,
                shouldModerate: shouldModerate,
            });
        },

        moderateChannel: function (channel, shouldModerate) {
            return this.moderate({
                entity: channel,
                type: 'channel',
                endpoint: 'channels',
                forumId: channel.primaryForum.id,
                shouldModerate: shouldModerate,
            });
        },


        /*
         * Blacklists a user globally or by forum and removes most recent comments/threads
         */
        confirmBlacklist: function (options) {
            confirmModal.confirmationPromise(confirmModal.TYPES.CONFIRM_CANCEL,
                confirmBlacklistUserTemplate(this.toJSON()))
                .then(_.bind(function (isConfirmed) {
                    if (!isConfirmed)
                        return;

                    this.addToBlacklist(options);
                }, this));
        },

        addToBlacklist: function (options) {
            options = options || {};
            var data = _.extend({}, options, {
                user: this.get('id'),
                retroactive: options && options.retroactive ? 1 : 0,
            });
            var url = data.forum ? 'blacklists/add.json' : 'blacklists/global/add.json';
            api.call(url, { method: 'POST', data: data })
                .done(_.bind(function (response) {
                    if (!response)
                        return;

                    if (data.forum)
                        this.trigger('blacklist:add', data.forum);
                    else
                        this.set('isOnGlobalBlacklist', true);

                    window.alert(gettext('User was added to the ban list')); // eslint-disable-line no-alert
                }, this))
                .error(function (response) {
                    // TODO provide a localized error because the server response will
                    // only be in English.
                    if (response && response.responseJSON)
                        window.alert(response.responseJSON.response); // eslint-disable-line no-alert
                });
        },

        removeFromBlacklist: function (options) {
            options = options || {};
            var data = _.extend({}, options, { user: this.get('id') });
            var url = data.forum ? 'blacklists/remove.json' : 'blacklists/global/remove.json';
            api.call(url, { method: 'POST', data: data })
                .done(_.bind(function (response) {
                    if (!response)
                        return;

                    if (data.forum)
                        this.trigger('blacklist:remove', data.forum);
                    else
                        this.set('isOnGlobalBlacklist', false);

                    window.alert(gettext('User was removed from the ban list')); // eslint-disable-line no-alert
                }, this))
                .error(function (response) {
                    // TODO provide a localized error because the server response will
                    // only be in English.
                    if (response && response.responseJSON)
                        window.alert(response.responseJSON.response); // eslint-disable-line no-alert
                });
        },

        requestSpamReview: function () {
            if (this.get('flaggedForSpamReview'))
                return;

            api.call('internal/users/requestSpamReview', { method: 'POST' })
                .done(_.bind(function () {
                    this.set('flaggedForSpamReview', true);
                }, this));
        },

        getDisplayName: function (attributes) {
            attributes = attributes || this.attributes;
            return attributes.name || attributes.username || 'Guest';
        },

        toJSON: function () {
            var result = parentProto.toJSON.apply(this, arguments);

            result.displayName = this.getDisplayName(result);

            return result;
        },
    });

    UniqueModel.addType('User', User);

    return User;
});

